import { useEffect, useRef } from 'react';
import { Header, Page } from 'redesign';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { settingsAlerts, useAlert } from 'shared/components/alerts';
import { useNavigate } from 'react-router-dom';
import { useCompanyRelativePath } from 'ogp/hooks';
import { routes } from 'ogp/Routes';
import { useTheme } from '@mui/material';
import { CompanyTheme, useThemeContext } from 'shared/contexts/theme-provider';
import { SettingEditThemeForm } from './setting-edit-theme-form';
import { SettingsEditThemeToolbox } from './setting-edit-theme-toolbox';
import { useUpdateCompanyTheme } from '../../../../../../hooks/queryHooks/companies/use-update-company-theme';

export type CompanyThemeSettingsForm = {
  primaryColor: string;
  secondaryColor: string;
  primaryAccent: string;
  secondaryAccent: string;
};

export const SettingsEditThemePageDataProvider = () => {
  return <SettingsEditTheme />;
};

export const SettingsEditTheme = () => {
  const { alertSuccess, alertError } = useAlert();
  const { t } = useTranslation('ogp-screen-specific');
  const { setTheme } = useThemeContext();
  const theme = useTheme();

  const mutation = useUpdateCompanyTheme();

  const generatePath = useCompanyRelativePath();
  const navigate = useNavigate();

  const originalTheme = useRef<CompanyTheme>({
    primaryColor: theme.palette.primary.main,
    secondaryColor: theme.palette.secondary.main,
    primaryAccent: theme.palette.lwPrimaryAccentLight[100],
    secondaryAccent: theme.palette.lwSecondaryAccentLight[100],
  });

  const formMethods = useForm<CompanyThemeSettingsForm>({
    defaultValues: {
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary.main,
      primaryAccent: theme.palette.lwPrimaryAccentLight[100],
      secondaryAccent: theme.palette.lwSecondaryAccentLight[100],
    },
  });

  const colorValues = useWatch({
    control: formMethods.control,
    name: ['primaryColor', 'secondaryColor', 'primaryAccent', 'secondaryAccent'],
  });

  useEffect(() => {
    const [primaryColor, secondaryColor, primaryAccent, secondaryAccent] = colorValues;

    setTheme({
      primaryColor: primaryColor ?? '',
      secondaryColor: secondaryColor ?? '',
      primaryAccent: primaryAccent ?? '',
      secondaryAccent: secondaryAccent ?? '',
    });
  }, [colorValues, setTheme]);

  const handleSubmit = () => {
    formMethods.handleSubmit((data: CompanyThemeSettingsForm) =>
      mutation.mutate(data, {
        onSuccess: () => {
          alertSuccess(t(settingsAlerts.success.updated));
          navigate(generatePath(routes.Settings));
        },
        onError: (e) => alertError(e),
      })
    )();
  };

  const handleCancel = () => {
    setTheme(originalTheme.current);
    formMethods.reset(originalTheme.current);
    navigate(generatePath(routes.Settings));
  };

  return (
    <>
      <form>
        <FormProvider {...formMethods}>
          <Page
            toolbox={
              <SettingsEditThemeToolbox handleSubmit={handleSubmit} handleCancel={handleCancel} />
            }
            header={<Header titleText={t('settings-edit-theme-page.title')} />}
          >
            <SettingEditThemeForm />
          </Page>
        </FormProvider>
      </form>
    </>
  );
};
